import FC24Player from "@/components/molecules/Player/FC24/FC24Player";
import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { HeroSection } from "@/containers/HeroSection";
import { capitalizeWords } from "@/functions/capitalizeFirstLetters";
import { extractColors } from "@/functions/extractCardColors";
import { chemStyles } from "@/functions/getChemStyles";
import { hexToRgba } from "@/functions/hexToRgba";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { MetaRatingIcon } from "@/icons";
import { owned_icon_true as ownedIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { DetailedPlayerType } from "../../DetailedPlayer";
import { Chemistry } from "../../reducer";
import AlternativeVersionsSection, {
  AlternativeVersion,
  AlternativeVersionType,
} from "../AlternativeVersionsSection";
import { ChemistryIcon } from "../Attributes/Chemistry/ChemistrySection";
import { CurrentState } from "../CurrentState";
import { EvolutionsButton } from "../Evolutions/EvolutionsButton";
import { MobileScrollTopSection } from "./MobileScrollTopSection";

export interface TopSectionProps {
  player: DetailedPlayerType;
  metaRating: number;
  metaRatingZeroChem: number;
  chemistry: Chemistry;
  chemStyleId: number;
  playerRoleId: number;
  rolesPlus: number[];
  name: string;
  versionName?: string;
  scrollPastTopBar: boolean;
}
export const TopSection = ({
  player,
  metaRating,
  metaRatingZeroChem,
  chemistry,
  chemStyleId,
  playerRoleId,
  rolesPlus,
  name,
  versionName,
  scrollPastTopBar,
}: TopSectionProps) => {
  const locationState = useLocation() as {
    state: { from: string; scrollPosition: number };
  };
  const location = useMemo(() => locationState.state, []);
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (location?.from) {
      if (location.scrollPosition) {
        window.scrollTo(0, location.scrollPosition);
      }
      navigate(location.from);
    } else {
      navigate("..");
    }
  };

  useEscape(handleNavigate);
  const versionAssets = useSelector(getVersionAssetsSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { strip } = extractColors({
    versionAssets,
    versionId: player?.versionId,
    metalId: player?.metalId,
  });

  return (
    <HeroSection
      title={`${name} ${versionName ? `- ${versionName}` : ""}`}
      titleBackgroundColor="bg-black/20"
      canonicalLink={window.location.href}
      shouldUseMobileTitle={true}
      onClose={handleNavigate}
      {...(player?.owned && {
        titleIcon: <div className="w-4 h-4 mr-2">{ownedIcon}</div>,
      })}
      {...(scrollPastTopBar
        ? {
            mobileChildren: (
              <MobileScrollTopSection
                player={player}
                metaRating={metaRating}
                metaRatingZeroChem={metaRatingZeroChem}
                chemistry={chemistry}
                chemStyleId={chemStyleId}
                playerRoleId={playerRoleId}
                rolesPlus={rolesPlus}
                name={name}
                scrollPastTopBar={scrollPastTopBar}
                onClose={handleNavigate}
              />
            ),
          }
        : {})}
    >
      <div
        className="h-[152px] mobile:h-[260px] flex flex-row mobile:grid grid-cols-4 max-w-content mx-auto w-full touch-device:gap-4 px-4 touch-device:px-10 large-desktop:px-20 mt-12 touch-device:mt-0"
        style={{
          background:
            player?.versionId !== undefined &&
            `linear-gradient(to right, var(--color-panel-base) 0%, ${hexToRgba(strip, 0.25)} 15%, var(--color-panel-base) 75%)`,
        }}
      >
        <div className="col-span-1 h-[175px] touch-device:h-[300px] -translate-y-4 mobile:translate-y-12 touch-device:translate-y-5">
          <div className="h-full w-[125px] mobile:w-[210px] mx-auto flex items-center justify-center">
            {player?.attributes && (
              <div
                className={cn(
                  "scale-150 transition-opacity duration-200 ease-in-out",
                  scrollPastTopBar && !isMobile ? "opacity-0" : "opacity-100",
                )}
              >
                <FC24Player
                  price={0}
                  chemStyleId={0}
                  isSbcPlayer={false}
                  isObjectivePlayer={false}
                  untradeable={false}
                  isMobile={isMobile}
                  hidePrice={true}
                  chemistry={{
                    currentChemistry: chemistry,
                    previousChemistry: chemistry,
                    isOutOfPosition: false,
                  }}
                  {...player}
                />
              </div>
            )}
          </div>
        </div>
        <CurrentState
          metaRating={metaRating}
          chemStyleId={chemStyleId}
          playerRoleId={playerRoleId}
          rolesPlus={rolesPlus}
          metaRatingZeroChem={metaRatingZeroChem}
          price={{
            price: player?.sbcPrice ?? player?.price,
            isObjectivePlayer: player?.isObjective,
            isSbcPlayer: player?.isSbc || player?.sbcPrice > 0,
          }}
        />
      </div>
    </HeroSection>
  );
};

export const TopSectionCompressed = ({
  playerName,
  metaRating,
  scrollPastTopBar,
  metaRatingZeroChem,
  playerRoleId,
  version,
  owned,
}: {
  playerName: string;
  metaRating: number;
  scrollPastTopBar: boolean;
  metaRatingZeroChem: number;
  playerRoleId: number;
  version: AlternativeVersionType;
  owned: boolean;
}) => {
  const tier = useSelector(tierSelector(metaRatingZeroChem, playerRoleId));
  return (
    <div
      className={cn(
        "text-sm mobile:text-base font-semibold text-dark-primary transition-opacity duration-100 flex flex-row items-center justify-between  gap-x-3 w-full pr-14",
        scrollPastTopBar ? "opacity-100" : "opacity-0",
      )}
    >
      <div className="flex flex-row items-center gap-x-1">
        <AlternativeVersion
          version={version}
          playerName={playerName}
          playerRoleId={playerRoleId}
          isActive={true}
        />
        <p className="whitespace-nowrap ml-1">{playerName}</p>
        {owned && <div className="w-4 h-4 mr-2">{ownedIcon}</div>}
      </div>
      <div className="flex flex-row items-center gap-x-2 text-dark-tertiary">
        <p className="text-sm text-dark-primary font-semibold">
          {metaRating?.toFixed(1)}
        </p>
        <MetaRatingIcon width="16" height="16" />
        <div className="w-0.5 h-5 bg-dark-disabled rounded-md" />
        <div className="flex flex-row items-center gap-x-1 text-dark-tertiary w-4">
          <p className={cn("text-base font-bold", tier?.tierTextColor)}>
            {tier?.tierName}
          </p>
        </div>
      </div>
    </div>
  );
};

export const DetailedPlayerToolbar = ({
  player,
  playerRoleId,
  resourceId,
  chemStyleId,
  chemistry,
  scrollPastTopBar,
  refTopBar,
  setIsAlternativeVersionsOpenMobile,
  isAlternativeVersionsOpenMobile,
  showEvolutionTree,
  setShowEvolutionTree,
  metaRating,
  metaRatingZeroChem,
  playerName,
  rolesPlus,
}: {
  player: DetailedPlayerType;
  playerRoleId: number;
  chemStyleId: number;
  chemistry: Chemistry;
  resourceId: number;
  scrollPastTopBar: boolean;
  refTopBar: React.MutableRefObject<HTMLDivElement>;
  setIsAlternativeVersionsOpenMobile: (open: boolean) => void;
  isAlternativeVersionsOpenMobile: boolean;
  showEvolutionTree: boolean;
  setShowEvolutionTree: React.Dispatch<React.SetStateAction<boolean>>;
  metaRating: number;
  metaRatingZeroChem: number;
  playerName: string;
  rolesPlus: number[];
}) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const rolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });
  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({ playerRoleId: playerRoleId }),
  );

  return isTouchDevice ? (
    <div className="flex flex-row justify-between items-center w-full gap-x-2">
      {scrollPastTopBar ? (
        <div
          className={cn(
            "flex flex-row gap-x-2 w-full h-9 items-center text-xs",
          )}
        >
          <div className="bg-white/5 h-9 px-1 rounded-sm flex flex-row items-center gap-x-1">
            <p>{playerRoleLabel}</p>
            <PlusPlus
              plus={rolesPlusOrPlusPlus === "plus"}
              plusPlus={rolesPlusOrPlusPlus === "plusPlus"}
            />
          </div>
          <div className="bg-white/5 h-9 px-1 rounded-sm flex flex-row items-center gap-x-1">
            <div
              className="text-lg text-dark-primary w-4"
              style={{
                fontFamily: "UltimateTeamIcons",
              }}
            >
              {chemStylesToIcon[chemStyleId]}
            </div>
            <p className="text-dark-primary">
              {capitalizeWords(chemStyles[chemStyleId])}
            </p>
            <ChemistryIcon
              chemistry={chemistry}
              selected={false}
              hovered={false}
            />
          </div>
        </div>
      ) : (
        <AlternativeVersionsSection
          alternativeVersions={player?.alternativeVersions}
          playerName={player?.name}
          playerRoleId={playerRoleId}
          resourceId={resourceId}
          hide={false}
          setIsAlternativeVersionsOpenMobile={
            setIsAlternativeVersionsOpenMobile
          }
          isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
        />
      )}
      <EvolutionsButton
        disabled={!player?.canEvolve}
        active={showEvolutionTree}
        small={scrollPastTopBar}
        onClick={() => {
          if (showEvolutionTree) {
            setShowEvolutionTree(false);
          } else {
            if (scrollPastTopBar) {
              window.scrollTo({
                top: refTopBar.current.offsetTop - 48,
                behavior: "smooth",
              });
            }
            setShowEvolutionTree((prev) => !prev);
          }
        }}
      />
    </div>
  ) : (
    <div className="w-full px-6 large-desktop:px-4 grid grid-cols-4 gap-4">
      <div className="col-span-1">
        {scrollPastTopBar && (
          <TopSectionCompressed
            playerName={playerName}
            metaRating={metaRating}
            scrollPastTopBar={scrollPastTopBar}
            metaRatingZeroChem={metaRatingZeroChem}
            playerRoleId={playerRoleId}
            owned={player?.owned}
            version={{
              versionId: player?.versionId,
              metalId: player?.metalId,
              rating: player?.rating,
              resourceId: resourceId,
            }}
          />
        )}
      </div>
      <div className="col-span-1">
        <AlternativeVersionsSection
          alternativeVersions={player?.alternativeVersions}
          playerName={player?.name}
          playerRoleId={playerRoleId}
          resourceId={resourceId}
          hide={scrollPastTopBar}
          setIsAlternativeVersionsOpenMobile={
            setIsAlternativeVersionsOpenMobile
          }
          isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
        />
      </div>
      <div className="col-span-2 ml-auto">
        <EvolutionsButton
          disabled={!player?.canEvolve}
          active={showEvolutionTree}
          onClick={() => {
            if (showEvolutionTree) {
              setShowEvolutionTree(false);
            } else {
              if (scrollPastTopBar) {
                window.scrollTo({
                  top: refTopBar.current.offsetTop - 48,
                  behavior: "smooth",
                });
              }
              setShowEvolutionTree((prev) => !prev);
            }
          }}
        />
      </div>
    </div>
  );
};
